import { useMemo } from "react";
import { BillView, BillViewSchema } from "@common/types/views/billViews";
import { PersistableView } from "@common/types/views/views";
import { BillsTaskTable } from "@components/tasks/TaskBody/Bills/BillsTaskTable";
import { getBillsTaskFilterTabs } from "@components/tasks/TaskBody/Bills/billsTaskFilters";
import { parseBillsTaskView } from "@components/tasks/TaskBody/Bills/billsTaskView";
import { withoutBulkActions } from "@components/tasks/TaskBody/shared/taskTableView";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import styles from "./CreateTaskDrawer.module.scss";
import { useTaskCreationPreviewView } from "./useTaskCreationPreviewView";

interface TaskCreationBillsPreviewProps {
    viewConfig: PersistableView;
}

interface PreviewBodyProps {
    initialView: BillView;
    isSelection: boolean;
}

const renderEmptyPreview = () => (
    <EmptyState header="No bills match this task" />
);

const PreviewBody = ({ initialView, isSelection }: PreviewBodyProps) => {
    const { view, handleFiltersChange, handleViewChange } =
        useTaskCreationPreviewView({
            initialView,
            viewSchema: BillViewSchema,
        });

    const availableFilters = useMemo(
        () =>
            isSelection
                ? []
                : getBillsTaskFilterTabs({ isWorkspaceScoped: true }),
        [isSelection],
    );

    return (
        <BillsTaskTable
            view={view}
            onViewChange={handleViewChange}
            onFiltersChange={handleFiltersChange}
            availableFilters={availableFilters}
            defaultView={initialView}
            lockScope={isSelection}
            hideEntityFilter
            emptyView={renderEmptyPreview}
        />
    );
};

/**
 * Left pane of the create task drawer for a BILLS task: the bills table the
 * task will show. Filters, search, date range and columns edited here are
 * written back to the form's `view`. A task created from a bills selection
 * shows exactly the selected bills and keeps its scope fixed.
 */
export const TaskCreationBillsPreview = ({
    viewConfig,
}: TaskCreationBillsPreviewProps) => {
    const initialView = useMemo(
        () => withoutBulkActions(parseBillsTaskView(viewConfig)),
        [viewConfig],
    );
    const isSelection = !!viewConfig.filters.billIds?.length;

    return (
        <div className={styles.previewPane}>
            <div className={styles.previewSurface}>
                <PreviewBody
                    initialView={initialView}
                    isSelection={isSelection}
                />
            </div>
        </div>
    );
};
